import { useState, useMemo, useEffect } from 'react';
import type { Attendance } from '@aegislab/shared';
import { toKstDateString } from '@aegislab/shared';
import {
  getLocalDateString,
  getLocalToday,
  WEEKDAYS,
  formatDateTimeShort,
  sumSegmentMs,
  formatDurationCompact,
} from '@/shared/lib/format';
// X3 단계 6: isWithin7Days 는 entities/attendance/lib/correctionWindow 로 lift됨 (D2 정정).
import { isWithin7Days } from '../lib/correctionWindow';
import * as s from './InlineCalendarPicker.css';

interface InlineCalendarPickerProps {
  records: Attendance[];
  selectedAttendanceId: string;
  onSelect: (attendanceId: string) => void;
  /** 관리자 모드: 7일 제한 해제 + 근태 없는 날짜도 선택 가능 */
  adminMode?: boolean;
  /** 관리자 모드에서 근태 없는 날짜 클릭 시 호출 */
  onDateSelect?: (date: string) => void;
  segmentMode?: boolean;
  pendingAttendanceIds?: ReadonlySet<string>;
  onMonthChange?: (month: string) => void;
  hideAbsentSessionLabel?: boolean;
  /** 처음 열 때 보여 줄 날짜(YYYY-MM-DD). 그 달로 열고 그 날을 고른 상태로 시작한다 */
  initialDate?: string;
}

export function InlineCalendarPicker({
  records,
  selectedAttendanceId,
  onSelect,
  adminMode = false,
  onDateSelect,
  segmentMode = false,
  pendingAttendanceIds,
  onMonthChange,
  hideAbsentSessionLabel = false,
  initialDate,
}: InlineCalendarPickerProps) {
  const today = getLocalToday();
  const now = new Date();

  const initialParts = initialDate ? initialDate.slice(0, 10).split('-').map(Number) : [];
  const hasInitialDate = initialParts.length === 3 && initialParts.every(Number.isFinite);
  const [viewYear, setViewYear] = useState(
    hasInitialDate ? (initialParts[0] as number) : now.getFullYear()
  );
  const [viewMonth, setViewMonth] = useState(
    hasInitialDate ? (initialParts[1] as number) : now.getMonth() + 1
  ); // 1-based
  const [selectedDate, setSelectedDate] = useState<string | null>(
    hasInitialDate ? (initialDate as string).slice(0, 10) : null
  );

  // records를 출근일(clockInAt) 기준으로 날짜별 그룹핑
  const recordsByDate = useMemo(() => {
    const map = new Map<string, Attendance[]>();
    for (const rec of records) {
      const dateKey =
        rec.status === 'ABSENT'
          ? rec.date.slice(0, 10)
          : getLocalDateString(new Date(rec.clockInAt));
      const list = map.get(dateKey);
      if (list) {
        list.push(rec);
      } else {
        map.set(dateKey, [rec]);
      }
    }
    return map;
  }, [records]);

  // 7일 이내 날짜 셋
  const eligibleDates = useMemo(() => {
    const set = new Set<string>();
    for (const [dateKey, recs] of recordsByDate) {
      if (recs.some((r) => isWithin7Days(r.clockInAt) && (!segmentMode || hasClosedSegment(r)))) {
        set.add(dateKey);
      }
    }
    return set;
  }, [recordsByDate, segmentMode]);

  // 캘린더 그리드 데이터
  const daysInMonth = new Date(viewYear, viewMonth, 0).getDate();
  const firstDayOfWeek = new Date(viewYear, viewMonth - 1, 1).getDay(); // 0=Sun

  const isCurrentMonth = viewYear === now.getFullYear() && viewMonth === now.getMonth() + 1;

  useEffect(() => {
    onMonthChange?.(`${viewYear}-${String(viewMonth).padStart(2, '0')}`);
  }, [viewYear, viewMonth, onMonthChange]);

  const goPrev = () => {
    if (viewMonth === 1) {
      setViewYear(viewYear - 1);
      setViewMonth(12);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const goNext = () => {
    if (viewMonth === 12) {
      setViewYear(viewYear + 1);
      setViewMonth(1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  // 날짜 선택 시 세션이 1개면 자동 선택
  const sessionsForDate = (selectedDate ? (recordsByDate.get(selectedDate) ?? []) : []).filter(
    (record) =>
      segmentMode
        ? isWithin7Days(record.clockInAt) && hasClosedSegment(record)
        : adminMode || isWithin7Days(record.clockInAt)
  );

  useEffect(() => {
    const single = sessionsForDate.length === 1 ? sessionsForDate[0] : null;
    if (single && single.id !== selectedAttendanceId) {
      onSelect(single.id);
    }
  }, [sessionsForDate, selectedAttendanceId, onSelect]);

  const handleDateClick = (dateStr: string) => {
    setSelectedDate(dateStr);
    const sessions = recordsByDate.get(dateStr) ?? [];
    if (sessions.length === 0 && adminMode) {
      // 관리자 모드: 근태 없는 날짜 선택 → 생성 모드
      onDateSelect?.(dateStr);
    }
  };

  const pad = (n: number) => String(n).padStart(2, '0');
  const windowStart = toKstDateString(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000));
  const windowEnd = toKstDateString(now);
  const [, startMonth, startDay] = windowStart.split('-').map(Number);
  const [, endMonth, endDay] = windowEnd.split('-').map(Number);
  const windowLabel = `${startMonth}월 ${startDay}일~${startMonth === endMonth ? '' : `${endMonth}월 `}${endDay}일`;

  return (
    <div className={s.wrapper}>
      {/* Month navigation */}
      <div className={s.monthHeader}>
        <button type="button" className={s.navButton} onClick={goPrev}>
          &lt;
        </button>
        <span className={s.monthLabel}>
          {viewYear}년 {viewMonth}월
        </span>
        <button type="button" className={s.navButton} onClick={goNext} disabled={isCurrentMonth}>
          &gt;
        </button>
      </div>

      {!adminMode && (
        <p className={s.correctionHint}>최근 7일 이내의 출퇴근 기록만 정정할 수 있습니다.</p>
      )}

      {/* Weekday labels */}
      <div className={s.weekdayRow}>
        {WEEKDAYS.map((day) => (
          <span key={day} className={s.weekdayLabel}>
            {day}
          </span>
        ))}
      </div>

      {/* Day grid */}
      <div className={s.dayGrid}>
        {/* Empty padding cells */}
        {Array.from({ length: firstDayOfWeek }, (_, i) => (
          <div key={`empty-${i}`} className={s.emptyCell} />
        ))}

        {/* Day cells */}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const day = i + 1;
          const dateStr = `${viewYear}-${pad(viewMonth)}-${pad(day)}`;
          const hasRecords = recordsByDate.has(dateStr);
          const isAbsent = recordsByDate.get(dateStr)?.every((r) => r.status === 'ABSENT');
          const isEligible = eligibleDates.has(dateStr);
          const isToday = dateStr === today;
          const isSelected = dateStr === selectedDate;
          const isFuture = dateStr > today;
          const isPending =
            recordsByDate.get(dateStr)?.some((record) => pendingAttendanceIds?.has(record.id)) ??
            false;
          const isInteractive = adminMode ? !isFuture : hasRecords && isEligible;
          let statusLabel = hasRecords
            ? isAbsent
              ? '출퇴근 기록 없음 · 결근'
              : '출근 기록 있음'
            : '';
          if (!adminMode && segmentMode && !isInteractive) {
            statusLabel = !isWithin7Days(`${dateStr}T00:00:00+09:00`)
              ? '정정 가능 기간 지남'
              : isAbsent
                ? '근무 구간 없음 · 결근'
                : '근무 구간 없음';
          } else if (!adminMode && hasRecords && !isEligible) {
            statusLabel += ', 정정 가능 기간 지남';
          }
          if (isPending) statusLabel += `${statusLabel ? ', ' : ''}정정 요청 검토 대기`;

          const cellClass = [
            s.dayCell,
            isInteractive ? s.dayCellInteractive : '',
            // dim(opacity 0.3)은 반드시 disabled 와 같은 조건이어야 한다. axe 는 진짜 disabled
            // 컨트롤만 색 대비 검사에서 빼주므로, 눌리는데 흐린 셀은 그대로 위반으로 잡힌다.
            // (admin 모드는 isInteractive=!isFuture 라 과거 기록일이 disabled={false} 였다.)
            // 유저 모드에서는 !isInteractive && hasRecords === hasRecords && !isEligible 로
            // 기존과 동일하다 — 흐려지는 날짜 집합은 그대로다.
            !isInteractive && hasRecords ? s.dayCellDisabled : '',
            isToday ? s.dayCellToday : '',
            isSelected ? s.dayCellSelected : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button
              key={dateStr}
              type="button"
              className={`${cellClass}${isPending ? ` ${s.dayCellPending}` : ''}`}
              disabled={!isInteractive}
              onClick={() => isInteractive && handleDateClick(dateStr)}
              aria-label={`${viewMonth}월 ${day}일${statusLabel ? ` (${statusLabel})` : ''}`}
              aria-pressed={isSelected}
            >
              {day}
              {isPending ? (
                <span className={s.pendingLabel}>대기</span>
              ) : (
                hasRecords && <span className={s.recordDot} />
              )}
            </button>
          );
        })}
      </div>

      {!adminMode && segmentMode && eligibleDates.size === 0 && (
        <p className={s.correctionHint}>
          지금은 정정할 수 있는 근무 구간이 없습니다. 최근 7일({windowLabel}) 안에 일한 기록이 있는
          날만 고를 수 있습니다.
        </p>
      )}

      {/* Session selector */}
      {selectedDate && sessionsForDate.length > 0 && (
        <div className={s.sessionList}>
          {!(hideAbsentSessionLabel && sessionsForDate.every((r) => r.status === 'ABSENT')) && (
            <span className={s.sessionLabel}>
              출퇴근 세션{sessionsForDate.length > 1 ? ' 선택' : ''}
              {sessionsForDate.every((r) => r.status === 'ABSENT')
                ? ' · 기록 없음'
                : ` · 총 ${formatDurationCompact(
                    sessionsForDate.reduce((sum, r) => sum + sumSegmentMs(r.segments, 'WORK'), 0)
                  )}`}
            </span>
          )}
          {sessionsForDate.length > 1 &&
            sessionsForDate.map((rec) => {
              const isActive = rec.id === selectedAttendanceId;
              return (
                <button
                  aria-current={isActive}
                  key={rec.id}
                  type="button"
                  className={`${s.sessionItem}${isActive ? ` ${s.sessionItemSelected}` : ''}`}
                  onClick={() => onSelect(rec.id)}
                >
                  <span>
                    {rec.status === 'ABSENT'
                      ? '출퇴근 기록 없음 · 결근'
                      : `${formatDateTimeShort(rec.clockInAt)} ~ ${rec.clockOutAt ? formatDateTimeShort(rec.clockOutAt) : '근무 중'}`}
                  </span>
                  <span className={s.sessionDuration}>
                    {rec.status === 'ABSENT'
                      ? '기록 없음'
                      : formatDurationCompact(sumSegmentMs(rec.segments, 'WORK'))}
                  </span>
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
}

function hasClosedSegment(record: Attendance): boolean {
  return (
    record.status !== 'ABSENT' &&
    record.segments.some(
      (segment) => segment.endAt !== null && (segment.type === 'WORK' || segment.type === 'BREAK')
    )
  );
}
